// components/UserTypeSelection/index.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';
import {
  Container,
  ContentWrapper,
  ReturnButton,
  SubmitButton,
  LogoImage,
  ChatBubbleContainer,
  ChatText,
  OptionsContainer,
  ButtonsWrapper,
  OptionButton,
  OptionIcon,
  LoadingOverlay,
  OptionsScrollContainer,
  LogoBubbleWrapper
} from './styles';

import {
  LastMeoChatBubbleContainer,
  LastMeoChatText
} from '../PresentationFeatures/styles';

import compassImage from '../../assets/meo_write.png';
import secondeIcon from '../../assets/top2_icon.png';
import premiereIcon from '../../assets/top1_icon.png';
import terminaleIcon from '../../assets/top_0.png';
import grande_ecole_post_bac_icon from '../../assets/grande_ecole_post_bac_icon.png';
import family_icon from '../../assets/family-icon.png';

import useDeviceType from '../useDeviceType';
import ProgressBar from '../ProgressBar';
import apiCall from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Custom smaller logo for parent guidance
const SmallerLogoImage = styled.img`
  width: 30%;
  height: 30%;
  object-fit: contain;
  margin-left: 35%;
  margin-top: 70%;

  /* Add drop shadow */
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));

  /* Add smooth transition for hover effect */
  transition: all 0.3s ease;

  /* Optional: Add subtle hover effect */
  &:hover {
    transform: scale(1.05) translateY(-2px);
    filter: drop-shadow(0 6px 8px rgba(0, 0, 0, 0.15));
    animation-play-state: paused;
  }

  /* Make animation smoother on higher-end devices */
  @media (prefers-reduced-motion: no-preference) {
    will-change: transform;
  }
`;

const UserTypeSelection = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const modifyProfile = window.location.href.includes('profile/modify');
  const isParentFlow = window.location.href.includes('parent-child-selection');
  const [selectedType, setSelectedType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const [showParentText, setShowParentText] = useState(isParentFlow);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showParentGuidance, setShowParentGuidance] = useState(false);
  
  const getFullText = () => {
    if (showParentGuidance) {
      return "Super ! Ce choix est décisif pour ton enfant et il doit lui correspondre à 100% ! À partir de maintenant, réponds à sa place pour que l'accompagnement soit plus réaliste.";
    }
    return (isParentFlow || showParentText) ? "En quelle classe est ton enfant ?" : "En quelle classe es-tu ?";
  };
  
  const fullText = getFullText();
  const { isMobile } = useDeviceType();
  // console.log(isMobile)
  // State to track pointer (active) states:
  const [activeOption, setActiveOption] = useState(null);
  const [submitActive, setSubmitActive] = useState(false);

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 15);
    return () => clearInterval(typingInterval);
  }, []);

  const allTypeOptions = [
    {
      value: 'parent',
      label: 'Parent',
      icon: family_icon,
      disabled: false
    },
    {
      value: 'seconde',
      label: 'Seconde',
      icon: secondeIcon,
      disabled: false
    },
    {
      value: 'premiere',
      label: 'Première',
      icon: premiereIcon,
      disabled: false
    },
    {
      value: 'terminale',
      label: 'Terminale',
      icon: terminaleIcon,
      disabled: false
    },
    {
      value: 'bac1',
      label: 'Bac +1',
      icon: grande_ecole_post_bac_icon,
      disabled: false
    },
  ];

  // Filter out parent option if we're in parent flow (selecting child's class) or after parent transition
  const typeOptions = (isParentFlow || showParentText) 
    ? allTypeOptions.filter(option => option.value !== 'parent')
    : allTypeOptions;

  // When an option is released, update the selection.
  const handleOptionClick = (option) => {
    if (!option.disabled && !isTransitioning) {
      setSelectedType(option.value);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || isTyping) return;
    
    // If parent is selected for the first time, transition to child selection
    if (selectedType === 'parent' && !showParentText) {
      setIsTransitioning(true);
      localStorage.setItem('parent', 'true');
      
      // Step 1: Fade out current options
      setTimeout(() => {
        setShowParentText(true);
        setSelectedType(null); // Reset selection for child selection
        // Reset typing animation with new text
        setText('');
        setIsTyping(true);
        let currentIndex = 0;
        const newFullText = "En quelle classe est ton enfant ?";
        const typingInterval = setInterval(() => {
          if (currentIndex <= newFullText.length) {
            setText(newFullText.slice(0, currentIndex));
            currentIndex++;
          } else {
            setIsTyping(false);
            // Step 2: Fade in new options after text is complete
            setTimeout(() => {
              setIsTransitioning(false);
            }, 300);
            clearInterval(typingInterval);
          }
        }, 15);
      }, 500); // Wait for fade out
      return; // Stop here, don't proceed to navigation yet
    }
    
    // If we're in parent flow and haven't shown guidance yet, show it first
    if ((isParentFlow || showParentText) && !showParentGuidance) {
      setShowParentGuidance(true);
      localStorage.setItem('type_utilisateur', selectedType);
      localStorage.setItem('parent', 'true');
      
      // Reset typing animation with guidance text
      setText('');
      setIsTyping(true);
      let currentIndex = 0;
      const guidanceText = "Super ! Ce choix est décisif pour ton enfant et il doit lui correspondre à 100% ! À partir de maintenant, réponds comme si tu étais ton enfant pour que l'accompagnement soit plus réaliste.";
      const typingInterval = setInterval(() => {
        if (currentIndex <= guidanceText.length) {
          setText(guidanceText.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 15);
      return; // Stop here, don't proceed to navigation yet
    }
    
    setIsLoading(true);
    try {
      if(modifyProfile){
        const requestData = {};
        
        if(isParentFlow || showParentGuidance) {
          // We're selecting the child's class in parent flow
          requestData.user_type = selectedType;
          requestData.parent = true;
        } else if(selectedType === 'parent') {
          // This shouldn't happen in modify profile, but just in case
          requestData.parent = true;
        } else {
          // Regular user selecting their own class
          requestData.user_type = selectedType;
          requestData.parent = false;
        }
        
        await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', requestData);
        let userData = JSON.parse(localStorage.getItem('user'));
        if(userData === null) {
          console.error('Error saving user type: user is null');
          return;
        }
        
        // Update local storage with the new data
        if(requestData.user_type) {
          userData.user_type = requestData.user_type;
        }
        if(requestData.parent !== undefined) {
          userData.parent = requestData.parent;
        }
        
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('need_update_ranking', 'true');
        localStorage.setItem('need_update_ranking_formation', 'true');
        await checkAuth();
        navigate('/profile/modify/type-bac');
      } else {
        // Handle parent selection vs child class selection
        if(selectedType === 'parent') {
          localStorage.setItem('parent', 'true');
          navigate('/parent-child-selection');
        } else if(isParentFlow || showParentGuidance) {
          // We're proceeding after parent guidance or child class selection
          const storedType = localStorage.getItem('type_utilisateur') || selectedType;
          const bacRedirectTypes = ['seconde', 'premiere', 'terminale'];
          const redirectPath = bacRedirectTypes.includes(storedType) 
            ? '/type-bac'
            : '/bac1';
          navigate(redirectPath);
        } else {
          localStorage.setItem('type_utilisateur', selectedType);
          localStorage.setItem('parent', 'false');
          const bacRedirectTypes = ['seconde', 'premiere', 'terminale'];
          const redirectPath = bacRedirectTypes.includes(selectedType) 
            ? '/type-bac'
            : '/bac1';
          navigate(redirectPath);
        }
      }
    } catch (error) {
      console.error('Error saving user type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (modifyProfile) {
      navigate('/profile');
    } else if (showParentGuidance) {
      // If we're in parent guidance, go back to child selection
      setShowParentGuidance(false);
      
      // Reset text to child selection question
      setText('');
      setIsTyping(true);
      let currentIndex = 0;
      const childText = "En quelle classe est ton enfant ?";
      const typingInterval = setInterval(() => {
        if (currentIndex <= childText.length) {
          setText(childText.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 15);
    } else if (showParentText && !isParentFlow) {
      // If we're in parent-child selection phase, go back to initial user type selection
      setShowParentText(false);
      setShowParentGuidance(false);
      setSelectedType(null);
      setIsTransitioning(false);
      
      // Reset text to initial question
      setText('');
      setIsTyping(true);
      let currentIndex = 0;
      const initialText = "En quelle classe es-tu ?";
      const typingInterval = setInterval(() => {
        if (currentIndex <= initialText.length) {
          setText(initialText.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 15);
      
      // Clear parent from localStorage
      localStorage.removeItem('parent');
    } else {
      navigate('/choice');
    }
  };

  // --- Submit Button Pointer Handlers ---
  const handleSubmitPressStart = () => {
    if(isLoading || isTyping || (!selectedType && !showParentGuidance)) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if(isLoading || isTyping || (!selectedType && !showParentGuidance)) return;
    setSubmitActive(false);
    setTimeout(() => {
      handleSubmit();
    }, 150);
  };

  return (
    <Container>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
      {!modifyProfile && !showParentGuidance && (
        <ProgressBar 
          startPercent={(isParentFlow || showParentText) ? 25 : 15} 
          endPercent={(isParentFlow || showParentText) ? 35 : 25} 
        />
      )}
      
      {showParentGuidance ? (
        // PresentationFeatures-style layout for parent guidance
        <>
          <LastMeoChatBubbleContainer>
            <LastMeoChatText isVisible={text.length > 0}>
              {text}
            </LastMeoChatText>
          </LastMeoChatBubbleContainer>

          <SmallerLogoImage src={compassImage} alt="Compass Logo" />
        </>
      ) : (
        // Original layout for user type selection
        <>
          <LogoBubbleWrapper>
            <LogoImage src={compassImage} alt="Compass Logo" />
            <ChatBubbleContainer isMobile={isMobile}>
              <ChatText isVisible={text.length > 0}>
                <span>{text}</span>
              </ChatText>
            </ChatBubbleContainer>
          </LogoBubbleWrapper>

          <OptionsScrollContainer isMobile={isMobile}>
            <OptionsContainer>
              <ButtonsWrapper style={{
                opacity: (isTyping || isTransitioning) ? 0 : 1,
                transition: 'opacity 0.5s ease',
                pointerEvents: (isTyping || isTransitioning) ? 'none' : 'auto'
              }}>
                {typeOptions.map((option) => (
                  <OptionButton
                    key={option.value}
                    $isSelected={selectedType === option.value}
                    $disabled={option.disabled}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    aria-pressed={selectedType === option.value}
                    aria-disabled={option.disabled}
                    // Pointer events to trigger pressed style
                    onMouseDown={() => { if (!option.disabled) setActiveOption(option.value); }}
                    onMouseUp={() => { if (!option.disabled) setActiveOption(null); }}
                    onMouseLeave={() => setActiveOption(null)}
                    onTouchStart={() => { if (!option.disabled) setActiveOption(option.value); }}
                    onTouchEnd={() => { if (!option.disabled) setActiveOption(null); }}
                    active={activeOption === option.value}
                  >
                    <OptionIcon
                      src={option.icon}
                      alt=""
                      $isSelected={selectedType === option.value}
                      style={{ opacity: option.disabled ? 0.5 : 1 }}
                    />
                    {option.label}
                  </OptionButton>
                ))}
              </ButtonsWrapper>
            </OptionsContainer>
          </OptionsScrollContainer>
        </>
      )}

        <SubmitButton
          type="button"
          disabled={isLoading || isTyping || (!selectedType && !showParentGuidance)}
          style={{ cursor: (isTyping || (!selectedType && !showParentGuidance)) ? 'not-allowed' : 'pointer' }}
          // Pointer events for the submit button:
          onMouseDown={handleSubmitPressStart}
          onMouseUp={handleSubmitPressEnd}
          onMouseLeave={() => setSubmitActive(false)}
          onTouchStart={handleSubmitPressStart}
          onTouchEnd={handleSubmitPressEnd}
          active={submitActive}
        >
          {isLoading ? 'Chargement...' : modifyProfile ? 'Valider' : 'Continuer'}
        </SubmitButton>
      </ContentWrapper>
    </Container>
  );
};

export default UserTypeSelection;
